import { useState, type ReactElement } from "react";
import { Download } from "lucide-react";
import { Button, Stack, Text } from "@ux/primitives";
import { palette } from "@ux/tokens";
import { csvDateStamp, triggerCsvDownload } from "./csv";
import { describeApiError } from "./errors";
import type { ExportJob } from "./exportCsv";

export interface ExportDataButtonProps {
    readonly filenamePrefix: string;
    readonly run: () => Promise<ExportJob>;
}

/**
 * Toolbar control that fetches the current filtered catalog (not just
 * the list-page window) and downloads it as CSV. Viewers can export;
 * the endpoints are reads. Truncation and failures surface inline so
 * a cramped toolbar doesn't hide a silent partial file.
 */
export function ExportDataButton({ filenamePrefix, run }: ExportDataButtonProps): ReactElement {
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    async function handleClick(): Promise<void> {
        setBusy(true);
        setMessage(null);
        try {
            const job = await run();
            triggerCsvDownload(`${filenamePrefix}-${csvDateStamp()}.csv`, job.csv);
            if (job.truncated) {
                setMessage(
                    `Downloaded ${job.rowCount.toLocaleString()} rows; the catalog is larger than the export cap and some rows may be missing.`,
                );
            }
        } catch (error) {
            setMessage(describeApiError(error));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Stack direction="row" gap={2} align="center" wrap>
            <Button
                variant="ghost"
                size="sm"
                leadingIcon={<Download size={14} />}
                onClick={() => {
                    void handleClick();
                }}
                disabled={busy}
                title="Download a CSV of the current filters for audit. Includes rows beyond the list-page cap."
            >
                {busy ? "Exporting…" : "Export data"}
            </Button>
            {message !== null ? (
                <Text role="muted" color={palette.danger}>
                    {message}
                </Text>
            ) : null}
        </Stack>
    );
}
